import { useState, useEffect, useCallback, useRef } from 'react'
import { X, SlidersHorizontal, FilterX, Filter, Search } from 'lucide-react'
import { useT } from '../../../app/lib/i18n'
import * as styles from './FilterBar.style.css'
import type { FilterBarProps, QuickFilterConfig } from './FilterBar.types'
import type { ColumnFiltersState } from './FilterBar.types'
import { QuickFilterControl, FilterConfigModal, QuickFiltersModal } from './components'

/** Update a single filter in the column filters state */
function setFilterValue(
  prev: ColumnFiltersState,
  id: string,
  value: unknown
): ColumnFiltersState {
  const rest = prev.filter((f) => f.id !== id)
  if (
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    value === false
  ) {
    return rest
  }
  return [...rest, { id, value }]
}

/** Get current value for a filter by id */
function getFilterValue(columnFilters: ColumnFiltersState, id: string): unknown {
  return columnFilters.find((f) => f.id === id)?.value
}

const SEARCH_FILTER_ID = 'search'
const initialVisibleIds = (quickFilters: { id: string }[], visibleQuickFilterIds?: string[]) =>
  visibleQuickFilterIds ?? quickFilters.map((f) => f.id)

export function FilterBar({
  columnFilters,
  onColumnFiltersChange,
  quickFilters,
  visibleQuickFilterIds,
  onVisibleQuickFiltersChange,
  columnConfig,
  searchPlaceholder,
  searchDebounceMs = 300,
}: FilterBarProps) {
  const t = useT()
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("shared.filter.search") + '...'
  const searchValue =
    (getFilterValue(columnFilters, SEARCH_FILTER_ID) as string) ?? ''
  const [localSearch, setLocalSearch] = useState(searchValue)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [visibleIds, setVisibleIds] = useState<string[]>(() =>
    initialVisibleIds(quickFilters, visibleQuickFilterIds)
  )
  const [configModalOpen, setConfigModalOpen] = useState(false)
  const [configModalDraft, setConfigModalDraft] = useState<string[]>([])
  const [quickFiltersModalOpen, setQuickFiltersModalOpen] = useState(false)

  // Sync visibleIds when quickFilters or visibleQuickFilterIds prop changes
  useEffect(() => {
    setVisibleIds(initialVisibleIds(quickFilters, visibleQuickFilterIds))
  }, [quickFilters, visibleQuickFilterIds])

  const visibleQuickFilters = visibleIds
    .map((id) => quickFilters.find((f) => f.id === id))
    .filter((f): f is QuickFilterConfig => f != null)

  const hasActiveFilters = columnFilters.some((f) => {
    const v = f.value
    return (
      v !== undefined &&
      v !== '' &&
      v !== false &&
      (typeof v !== 'string' || v.trim() !== '') &&
      (!Array.isArray(v) || v.length > 0)
    )
  })

  // Sync local search when external columnFilters change (e.g. reset)
  useEffect(() => {
    setLocalSearch(searchValue)
  }, [searchValue])

  const applySearch = useCallback(
    (value: string) => {
      onColumnFiltersChange((prev) => setFilterValue(prev, SEARCH_FILTER_ID, value))
    },
    [onColumnFiltersChange]
  )

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      applySearch(localSearch)
      debounceRef.current = null
    }, searchDebounceMs)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [localSearch, searchDebounceMs, applySearch])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(e.target.value)
  }

  const handleClearSearch = () => {
    setLocalSearch('')
    applySearch('')
  }

  const handleQuickFilterChange = (id: string, value: unknown) => {
    onColumnFiltersChange((prev) => setFilterValue(prev, id, value))
  }

  const handleConfigClick = () => {
    setConfigModalDraft([...visibleIds])
    setConfigModalOpen(true)
  }

  const handleFiltersButtonClick = () => {
    setConfigModalOpen(false)
    setQuickFiltersModalOpen(true)
  }

  const handleClearFilters = () => {
    setLocalSearch('')
    onColumnFiltersChange(() => [])
  }

  const handleConfigSave = () => {
    const removedIds = visibleIds.filter((id) => !configModalDraft.includes(id))
    setVisibleIds(configModalDraft)
    if (removedIds.length > 0) {
      onColumnFiltersChange((prev) =>
        prev.filter((f) => f.id === SEARCH_FILTER_ID || configModalDraft.includes(f.id))
      )
    }
    onVisibleQuickFiltersChange?.(configModalDraft)
  }

  return (
    <div className={styles.filterBar}>
      <div className={styles.searchWrapper}>
        <span className={styles.searchIcon}><Search size={14} /></span>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={resolvedSearchPlaceholder}
          value={localSearch}
          onChange={handleSearchChange}
          aria-label={t("shared.filter.search")}
        />
        {localSearch && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClearSearch}
            aria-label={t("shared.filter.clearSearch")}
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className={styles.quickFiltersArea}>
        <div className={styles.quickFiltersStack}>
          {visibleQuickFilters.map((config) => (
            <QuickFilterControl
              key={config.id}
              config={config}
              value={getFilterValue(columnFilters, config.id)}
              onChange={(v) => handleQuickFilterChange(config.id, v)}
            />
          ))}
        </div>
      </div>

      <div className={styles.filterBarRow2}>
        <div className={styles.filterBarActionsLeft}>
          <button
            type="button"
            className={styles.filtersButton}
            onClick={handleFiltersButtonClick}
            aria-label={t("shared.filter.filters")}
          >
            <Filter size={14} />
            <span>{t("shared.filter.filters")}</span>
          </button>
        </div>
        <div className={styles.filterBarActions}>
          {hasActiveFilters && (
            <button
              type="button"
              className={styles.filtersConfigButton}
              onClick={handleClearFilters}
              aria-label={t("shared.filter.resetFilters")}
            >
              <FilterX size={14} />
              {t("shared.filter.clear")}
            </button>
          )}
          <button
            type="button"
            className={styles.filtersConfigButton}
            onClick={handleConfigClick}
            aria-label={t("shared.filter.configureFilters")}
          >
            <SlidersHorizontal size={14} />
            {t("shared.filter.filters")}
          </button>
        </div>
      </div>

      <QuickFiltersModal
        open={quickFiltersModalOpen}
        onOpenChange={setQuickFiltersModalOpen}
        quickFilters={visibleQuickFilters}
        getFilterValue={(id) => getFilterValue(columnFilters, id)}
        onFilterChange={handleQuickFilterChange}
      />
      <FilterConfigModal
        open={configModalOpen}
        onOpenChange={setConfigModalOpen}
        quickFilters={quickFilters}
        selectedIds={configModalDraft}
        onSelectedIdsChange={setConfigModalDraft}
        onSave={handleConfigSave}
        columnConfig={columnConfig}
      />
    </div>
  )
}

export type { FilterBarProps, QuickFilterConfig, QuickFilterOption, RangeFilterOption, ColumnConfig } from './FilterBar.types'
export type { ColumnFilter, ColumnFiltersState } from './FilterBar.types'
export { getQuickFiltersFromColumns } from './getQuickFiltersFromColumns'
export type { FilterColumnMeta } from './getQuickFiltersFromColumns'
